import { useEffect, useState, useMemo, useRef} from "react";
import {
  Plus,
  MessageSquare,
  Save,
  Trash2,
  Loader2,
  Tag,
  X,
  Search
} from "lucide-react";
import { toast } from "react-toastify";

import {
  createQueryInstruction,
  getQueryInstructionsBySource,
  getSqlValidationContext,
  updateQueryInstruction,
  deleteQueryInstruction,
  validateKnowledgeSql,
} from "../../../../apiWithKnowledge";
import SqlValidationEditor from "./SqlValidationEditor.jsx";
import { rewriteSqlForEditor } from "./sqlReferenceTransforms.js";
import { useIngestionJobs } from "../../ingestion/IngestionJobsContext";
import { IngestionQueueStrip, IngestionStatusBadge } from "../../ingestion/IngestionFeedback";

function firstValidationMessage(response) {
  return response?.data?.diagnostics?.[0]?.message || "Please fix the SQL validation issues before saving.";
}

export default function QueryInstructionsTab({ sourceDbId }) {
  const createButtonRef = useRef(null);
  const [queries, setQueries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [referenceSchema, setReferenceSchema] = useState({});

  /* --------------------------------------------------
     Memoized Search Filter
  -------------------------------------------------- */
  const filteredQueries = useMemo(() => {
    if (!searchQuery.trim()) return queries;
    
    const lower = searchQuery.toLowerCase();
    return queries.filter((q) => {
      const matchQuestion = (q.question_text || "").toLowerCase().includes(lower);
      const matchSql = (q.sql_query || "").toLowerCase().includes(lower);
      const matchTags = Array.isArray(q.tags) && q.tags.some(t => t.toLowerCase().includes(lower));
      
      return matchQuestion || matchSql || matchTags;
    });
  }, [queries, searchQuery]);

  // Form state
  const [editingId, setEditingId] = useState(null);
  const [questionText, setQuestionText] = useState("");
  const [sqlQuery, setSqlQuery] = useState("");
  const [guidanceText, setGuidanceText] = useState("");
  const [tags, setTags] = useState("");
  const [saving, setSaving] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [saveValidating, setSaveValidating] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({ questionText: "", sqlQuery: "" });
  const { registerJob, getJob, retryJob } = useIngestionJobs({
    sourceDbId,
    resourceTypes: ["query_instruction"],
  });

  /* --------------------------------------------------
     Load queries
  -------------------------------------------------- */
  useEffect(() => {
    if (!sourceDbId) return;

    setLoading(true);
    Promise.all([
      getSqlValidationContext(sourceDbId).catch(() => ({ data: null })),
      getQueryInstructionsBySource(sourceDbId),
    ])
      .then(([contextRes, queryRes]) => {
        const schema = contextRes?.data?.schema || {};
        setReferenceSchema(schema);
        setQueries(
          (queryRes?.data || []).map((item) => ({
            ...item,
            sql_query: rewriteSqlForEditor(item.sql_query, schema),
          })),
        );
      })
      .catch(() => toast.error("Failed to load query instructions"))
      .finally(() => setLoading(false));
  }, [sourceDbId]);

  /* --------------------------------------------------
     Reset form
  -------------------------------------------------- */
  const resetForm = () => {
    setEditingId(null);
    setQuestionText("");
    setSqlQuery("");
    setGuidanceText("");
    setTags("");
    setShowCreateForm(false);
    setSaveValidating(false);
    setFieldErrors({ questionText: "", sqlQuery: "" });
  };

  const closeForm = () => {
    resetForm();
    window.setTimeout(() => createButtonRef.current?.focus(), 0);
  };

  /* --------------------------------------------------
     Save
  -------------------------------------------------- */
  const handleSave = async () => {
    const nextErrors = {
      questionText: questionText.trim() ? "" : "Question is required",
      sqlQuery: sqlQuery.trim() ? "" : "SQL query is required",
    };
    setFieldErrors(nextErrors);

    if (nextErrors.questionText || nextErrors.sqlQuery) {
      return;
    }

    setSaveValidating(true);
    try {
      const validationResponse = await validateKnowledgeSql({
        source_db_id: sourceDbId,
        sql: sqlQuery,
        mode: "query_instruction",
      });

      if (!validationResponse?.data?.valid) {
        toast.error(firstValidationMessage(validationResponse));
        return;
      }
    } catch {
      toast.error("SQL validation failed. Please try again.");
      return;
    } finally {
      setSaveValidating(false);
    }

    setSaving(true);
    try {
      const payload = {
        source_db_id: sourceDbId,
        question_text: questionText,
        sql_query: sqlQuery,
        guidance_text: guidanceText,
        tags: tags
          .split(",")
          .map((t) => t.trim())
          .filter(Boolean),
      };

      const response = editingId
        ? await updateQueryInstruction(editingId, payload)
        : await createQueryInstruction(payload);
      const saved = response?.data?.data;
      if (saved) {
        const normalized = {
          ...saved,
          sql_query: rewriteSqlForEditor(saved.sql_query, referenceSchema),
        };
        setQueries((current) => editingId
          ? current.map((item) => String(item.id) === String(saved.id) ? normalized : item)
          : [normalized, ...current]);
        if (response?.data?.ingestion) {
          registerJob({
            summary: response.data.ingestion,
            sourceDbId,
            resourceId: saved.id,
            label: saved.question_text,
          });
        }
      }
      toast.success(editingId ? "Query instruction updated" : "Query instruction created");
      closeForm();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to save query instruction");
    } finally {
      setSaving(false);
    }
  };

  /* --------------------------------------------------
     Edit
  -------------------------------------------------- */
  const handleEdit = (q) => {
    setEditingId(q.id);
    setQuestionText(q.question_text);
    setSqlQuery(q.sql_query);
    setGuidanceText(q.guidance_text || "");
    setTags((q.tags || []).join(", "));
    setShowCreateForm(true);
  };

  /* --------------------------------------------------
     Delete
  -------------------------------------------------- */
  const handleDelete = async (id) => {
    if (!window.confirm("Delete this query instruction?")) return;

    try {
      const query = queries.find((item) => String(item.id) === String(id));
      const response = await deleteQueryInstruction(id);
      setQueries((prev) => prev.filter((q) => q.id !== id));
      if (response?.data?.ingestion) {
        registerJob({
          summary: response.data.ingestion,
          sourceDbId,
          resourceId: id,
          label: query?.question_text || "Reference query update",
        });
      }
      toast.success("Query instruction deleted");
    } catch {
      toast.error("Failed to delete query instruction");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-muted-foreground">
        <Loader2 className="w-4 h-4 animate-spin" />
        Loading query instructions…
      </div>
    );
  }

  return (
    <div className="space-y-8 max-w-6xl">

      {/* ================= STICKY HEADER & SEARCH ================= */}
      <div className="sticky top-[-8px] z-20 bg-background py-3 mb-4 border-b border-border flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        
        <h3 className="text-sm font-semibold flex items-center gap-2 shrink-0">
          <MessageSquare className="w-4 h-4" />
          Existing Query Instructions
          <span className="text-xs font-normal text-muted-foreground bg-muted px-2 py-0.5 rounded-full ml-1">
            {filteredQueries.length}
          </span>
        </h3>

        <div className="flex items-center gap-3 w-full sm:w-auto justify-end">
          {/* Search Bar - Side-by-side */}
          <div className="relative w-full sm:w-72">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by question, SQL, or tags..."
              className="w-full border border-border rounded-md pl-9 pr-8 py-1.5 text-sm bg-background text-foreground focus:outline-none focus:ring-1 focus:ring-primary transition-all"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery("")}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground p-1"
              >
                <X className="w-3 h-3" />
              </button>
            )}
          </div>

          {/* Create Button */}
          <button
            ref={createButtonRef}
            onClick={() => {
              resetForm();
              setShowCreateForm(true);
            }}
            className="inline-flex items-center gap-2 px-3 py-1.5 bg-primary text-primary-foreground rounded-md text-sm hover:bg-primary/90 transition-colors shrink-0 whitespace-nowrap"
          >
            <Plus className="w-4 h-4" />
            Create
          </button>
        </div>
      </div>

      {/* ================= MODAL ================= */}
      {showCreateForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 animate-in fade-in duration-300 ">
          <div className="bg-background rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col border border-border overflow-hidden ">
            {/* Header */}
            <div className="flex justify-between items-center p-6 border border-border">
              <h2 className="text-xl font-semibold">
                {editingId ? "Edit Query Instruction" : "Create Query Instruction"}
              </h2>
              <button
                onClick={closeForm}
                className="p-1 rounded-full hover:bg-muted transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Content */}
            {/* ================= CONTENT ================= */}
             <div className="flex-1 min-h-0 overflow-y-auto custom-scrollbar pb-8 mr-4 ml-4 mt-2 mb-2">
              <div className="space-y-5">
                {/* Question */}
                <textarea
                  rows={2}
                  value={questionText}
                  onChange={(e) => {
                    setQuestionText(e.target.value);
                    if (fieldErrors.questionText) {
                      setFieldErrors((prev) => ({ ...prev, questionText: "" }));
                    }
                  }}
                  placeholder="What is the current Portfolio at Risk (PAR) percentage?"
                  className={`w-full rounded-md px-3 py-2 text-sm bg-background text-foreground ${
                    fieldErrors.questionText
                      ? "border border-red-500 focus:outline-none focus:ring-1 focus:ring-red-500"
                      : "border border-border"
                  }`}
                />
                {fieldErrors.questionText && (
                  <p className="text-xs text-red-600 dark:text-red-400">{fieldErrors.questionText}</p>
                )}

                {/* SQL */}
                <SqlValidationEditor
                  sourceDbId={sourceDbId}
                  mode="query_instruction"
                  value={sqlQuery}
                  onValueChange={(nextValue) => {
                    setSqlQuery(nextValue);
                    if (fieldErrors.sqlQuery) {
                      setFieldErrors((prev) => ({ ...prev, sqlQuery: "" }));
                    }
                  }}
                  rows={7}
                  placeholder="SELECT SUM(CASE WHEN Current_DPD > 30 THEN Outstanding_Amount ELSE 0 END) / SUM(Outstanding_Amount) FROM X_Bucket"
                  minHeightClassName="min-h-[220px]"
                  requiredMessage={fieldErrors.sqlQuery}
                />

                {/* Guidance */}
                <textarea
                  rows={2}
                  value={guidanceText}
                  onChange={(e) => setGuidanceText(e.target.value)}
                  placeholder="Optional explanation of how this query works"
                  className="w-full border border-border rounded-md px-3 py-2 text-sm bg-background text-foreground"
                />

                {/* Tags */}
                <div className="relative">
                  <Tag className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-foreground" />
                  <input
                    value={tags}
                    onChange={(e) => setTags(e.target.value)}
                    placeholder="Tags (comma separated)"
                    className="w-full border border-border rounded-md pl-9 pr-3 py-2 text-sm bg-background text-foreground"
                  />
                </div>
              </div>
            </div>

            {/* Footer Actions */}
            <div className="px-6 py-4 border border-border bg-muted/30">
              <div className="flex justify-end gap-2">
                <button
                     onClick={closeForm}
                  className="px-4 py-2 text-sm border border-border rounded-md bg-background text-foreground hover:bg-muted"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  disabled={saving || saveValidating}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-md text-sm"
                >
                  {saving || saveValidating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  {editingId ? "Update" : "Save"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <IngestionQueueStrip sourceDbId={sourceDbId} resourceTypes={["query_instruction"]} />

      {/* ================= LIST ================= */}
      <div className="space-y-3">

        {queries.length === 0 && (
          <p className="text-sm text-muted-foreground">
            No query instructions defined yet.
          </p>
        )}

        {filteredQueries.map((q) => (
          <div
            key={q.id}
            className="border border-border rounded-lg p-4 flex justify-between items-start"
          >
            <div className="space-y-1 text-sm">
              <div className="font-medium flex flex-wrap items-center gap-2">
                {q.question_text}
                <IngestionStatusBadge
                  compact
                  job={getJob("query_instruction", q.id)}
                  onRetry={() => retryJob(getJob("query_instruction", q.id)?.jobId)}
                  onReview={() => handleEdit(q)}
                />
              </div>
              <code className="block text-xs text-muted-foreground">
                {q.sql_query}
              </code>
              {/* Ensure tags is an array before mapping */}
{Array.isArray(q.tags) && q.tags.length > 0 && (
  <div className="flex flex-wrap gap-1 mt-1">
    {q.tags.map((t, i) => (
      <span
        key={i}
        className="text-xs bg-muted px-2 py-0.5 rounded"
      >
        {t}
      </span>
    ))}
  </div>
)}
            </div>

            <div className="flex gap-1">
              <button
                onClick={() => handleEdit(q)}
                className="px-2 py-1 text-xs border border-border  rounded"
              >
                Edit
              </button>
              <button
                onClick={() => handleDelete(q.id)}
                className="text-destructive hover:bg-destructive/10 rounded p-2"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
